import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Button } from "@gately/ui/button";
import { DetailList } from "@gately/ui/detail-list";
import {
  DetailSheet,
  DetailSheetBody,
  DetailSheetFooter,
  DetailSheetHeader,
} from "@gately/ui/detail-sheet";
import { EmptyState } from "@gately/ui/empty-state";
import { SettingsSection } from "@gately/ui/settings";
import { Textarea } from "@gately/ui/textarea";
import { toast } from "@gately/ui/toast";
import {
  formVisitorTypeLabels,
  groupReviewQueue,
  hasIdNumberAnswers,
  purgeSharedWarning,
  reviewGroupTitle,
  reviewManySummary,
  reviewManyTone,
} from "@gately/validators";

import { FormRenderer } from "~/components/forms/FormRenderer";
import { orpc } from "~/lib/orpc";

export const Route = createFileRoute("/_dashboard/reglages/validations")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      orpc.form.listSubmissions.queryOptions({ input: { status: "pending" } }),
    ),
  component: ValidationsPage,
});

function ValidationsPage() {
  const queryClient = useQueryClient();
  const { data: pending } = useSuspenseQuery(
    orpc.form.listSubmissions.queryOptions({ input: { status: "pending" } }),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: orpc.form.listSubmissions.key(),
    });

  const reviewOne = useMutation(
    orpc.form.review.mutationOptions({
      onSuccess: async () => {
        toast.success("Décision enregistrée.");
        await refresh();
      },
      onError: async (err) => {
        toast.error(err.message || "Échec de la décision.");
        await refresh();
      },
    }),
  );
  const reviewAll = useMutation(
    orpc.form.reviewMany.mutationOptions({
      onSuccess: async (res) => {
        toast[reviewManyTone(res)](reviewManySummary(res, "approve"));
        await refresh();
      },
      onError: async (err) => {
        toast.error(err.message || "Échec de la validation groupée.");
        await refresh();
      },
    }),
  );
  const busy = reviewOne.isPending || reviewAll.isPending;
  const groups = groupReviewQueue(pending);

  return (
    <SettingsSection
      title="Validations"
      hint="Demandes en attente d'approbation, par invitation."
      surface={false}
    >
      {groups.length === 0 ? (
        <EmptyState
          title="Aucune demande en attente."
          description="Les demandes envoyées depuis un formulaire apparaissent ici pour approbation."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((g) => {
            const s = g.first;
            const type =
              (formVisitorTypeLabels as Record<string, string | undefined>)[
                s.visitorType
              ] ?? s.visitorType;
            // The company name for an office unit wins, as in `unitLabel()`.
            const unit =
              s.unitName ??
              (s.number
                ? `${s.building ? `${s.building} — ` : ""}${s.number}`
                : null);
            return (
              <div
                key={g.inviteId}
                className="rounded-lg border border-neutral-200 bg-white"
              >
                <div className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {reviewGroupTitle(g)}
                    </div>
                    <div className="truncate text-xs text-neutral-500">
                      {[
                        type,
                        unit,
                        s.submittedByName ? `par ${s.submittedByName}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      {" · "}
                      {new Date(s.createdAt).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  {g.pendingIds.length > 1 ? (
                    <Button
                      size="sm"
                      className="ml-auto"
                      disabled={busy}
                      onClick={() =>
                        reviewAll.mutate({
                          submissionIds: g.pendingIds,
                          decision: "approve",
                        })
                      }
                    >
                      Tout valider ({g.pendingIds.length})
                    </Button>
                  ) : null}
                </div>
                {g.rows.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-2 px-4 py-2.5"
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedId(r.id)}
                      className="min-w-0 flex-1 truncate text-left text-sm hover:underline"
                    >
                      {r.guestName}
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        reviewOne.mutate({
                          submissionId: r.id,
                          decision: "reject",
                        })
                      }
                    >
                      Refuser
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        reviewOne.mutate({
                          submissionId: r.id,
                          decision: "approve",
                        })
                      }
                    >
                      Approuver
                    </Button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {selectedId ? (
        <SubmissionDetail
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onReviewed={async () => {
            setSelectedId(null);
            await refresh();
          }}
        />
      ) : null}
    </SettingsSection>
  );
}

function SubmissionDetail({
  id,
  onClose,
  onReviewed,
}: {
  id: string;
  onClose: () => void;
  onReviewed: () => Promise<void> | void;
}) {
  const queryClient = useQueryClient();
  const { data: sub, isError } = useQuery(
    orpc.form.getSubmission.queryOptions({ input: { id } }),
  );
  const [note, setNote] = useState("");

  const review = useMutation(
    orpc.form.review.mutationOptions({
      onSuccess: async () => {
        toast.success("Décision enregistrée.");
        await onReviewed();
      },
      onError: (err) => toast.error(err.message || "Échec de la décision."),
    }),
  );

  const invalidateSubmission = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: orpc.form.getSubmission.key({ input: { id } }),
      }),
      queryClient.invalidateQueries({
        queryKey: orpc.form.listSubmissions.key(),
      }),
    ]);

  const deleteUpload = useMutation(
    orpc.form.deleteUpload.mutationOptions({
      onSuccess: async () => {
        toast.success("Document supprimé.");
        await invalidateSubmission();
      },
      onError: (err) => toast.error(err.message || "Échec de la suppression."),
    }),
  );

  const purgeDocuments = useMutation(
    orpc.form.purgeSubmissionDocuments.mutationOptions({
      onSuccess: async (res) => {
        toast.success(
          res.deleted > 0
            ? "Documents supprimés."
            : "N° de pièce d'identité effacé.",
        );
        await invalidateSubmission();
      },
      onError: (err) => toast.error(err.message || "Échec de la suppression."),
    }),
  );

  function handleDeleteUpload(uploadId: string) {
    const reason = window.prompt("Motif de suppression (CNDP, etc.)");
    if (!reason?.trim()) return;
    deleteUpload.mutate({ uploadId, reason: reason.trim() });
  }

  function handlePurgeAll() {
    const shared = purgeSharedWarning(sub?.sharedDocumentCount ?? 0);
    if (shared && !window.confirm(shared)) return;
    const reason = window.prompt("Motif de suppression (CNDP, etc.)");
    if (!reason?.trim()) return;
    purgeDocuments.mutate({ submissionId: id, reason: reason.trim() });
  }

  const state = sub ? "ready" : isError ? "error" : "loading";

  function renderReady() {
    if (!sub) return null;

    const uploadsById = new Map(sub.uploads.map((u) => [u.id, u]));
    const activeUploads = sub.uploads.filter((u) => u.status !== "deleted");
    // A typed CIN number is identity data too: an upload-less dossier still
    // gets the purge action (the API's zero-upload branch redacts + audits).
    const idNumbers = hasIdNumberAnswers(sub.schemaSnapshot, sub.answers);
    const purgeButton =
      activeUploads.length > 0 || idNumbers ? (
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={purgeDocuments.isPending}
          onClick={handlePurgeAll}
        >
          {activeUploads.length > 0
            ? "Supprimer tous les documents"
            : "Effacer le N° de pièce d'identité"}
        </Button>
      ) : null;

    return (
      <>
        <DetailSheetHeader title={sub.guestName} />
        <DetailSheetBody>
          <div className="mb-4 rounded-lg border border-neutral-200 p-4">
            <FormRenderer
              schema={sub.schemaSnapshot}
              value={sub.answers}
              onChange={() => undefined}
              disabled
              resolveUpload={(uploadId) => uploadsById.get(uploadId)}
            />
          </div>
          {sub.uploads.length > 0 || idNumbers ? (
            <div className="mb-4 flex flex-col gap-2 rounded-lg border border-neutral-200 p-4">
              <DetailList
                title="Documents"
                emptyLabel="Aucun document"
                actions={purgeButton}
                items={sub.uploads.map((u) =>
                  u.status === "deleted" || !u.url
                    ? {
                        key: u.id,
                        left: (
                          <span className="text-neutral-500 italic">
                            {u.fileName ?? u.fieldKey}
                          </span>
                        ),
                        right:
                          u.status === "deleted" ? "Supprimé" : "Indisponible",
                      }
                    : {
                        key: u.id,
                        left: (
                          <a
                            href={u.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline"
                          >
                            {u.fileName ?? u.fieldKey}
                          </a>
                        ),
                        right: null,
                        action: (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={deleteUpload.isPending}
                            onClick={() => handleDeleteUpload(u.id)}
                          >
                            Supprimer
                          </Button>
                        ),
                      },
                )}
              />
            </div>
          ) : null}
          <div className="mb-3 flex flex-col gap-1">
            <label htmlFor="validation-refuse-note" className="text-sm">
              Motif (en cas de refus)
            </label>
            <Textarea
              id="validation-refuse-note"
              className="min-h-16"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </DetailSheetBody>
        <DetailSheetFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="destructive"
            disabled={review.isPending}
            onClick={() =>
              review.mutate({
                submissionId: id,
                decision: "reject",
                note: note.trim() || undefined,
              })
            }
          >
            Refuser
          </Button>
          <Button
            disabled={review.isPending}
            onClick={() =>
              review.mutate({ submissionId: id, decision: "approve" })
            }
          >
            Approuver
          </Button>
        </DetailSheetFooter>
      </>
    );
  }

  return (
    <DetailSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width="lg"
      state={state}
      placeholderTitle="Demande"
      errorLabel="Demande introuvable."
    >
      {renderReady()}
    </DetailSheet>
  );
}
